/* ============================================
   THEME CONTEXT
   The Creative Orchestra - GEMINI Framework
   Manages dynamic theming for work pages
   ============================================ */

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { workThemes, defaultTheme, novelTheme, getWorkTheme, applyThemeVariables } from '@utils/workThemes';

// Create context
const ThemeContext = createContext(null);

/**
 * Theme Provider Component
 * Manages the active theme based on current page/work
 */
export const ThemeProvider = ({ children }) => {
  // State
  const [activeThemeId, setActiveThemeId] = useState('default');
  const [isTransitioning, setIsTransitioning] = useState(false);

  /**
   * Get the current theme object
   */
  const activeTheme = useMemo(() => {
    if (activeThemeId === 'default') return defaultTheme;
    if (activeThemeId === 'novel') return novelTheme;
    return workThemes[activeThemeId] || defaultTheme;
  }, [activeThemeId]);

  /**
   * Set theme by ID with optional transition
   */
  const setTheme = useCallback((themeId, withTransition = true) => {
    if (themeId === activeThemeId) return;

    if (withTransition) {
      setIsTransitioning(true);
      
      // Short delay for transition effect
      setTimeout(() => {
        setActiveThemeId(themeId);
        
        // End transition after theme is applied
        setTimeout(() => {
          setIsTransitioning(false);
        }, 300);
      }, 150);
    } else {
      setActiveThemeId(themeId);
    }
  }, [activeThemeId]);

  /**
   * Set theme for a specific work
   */
  const setWorkTheme = useCallback((workId) => {
    if (workThemes[workId]) {
      setTheme(workId);
    } else {
      console.warn(`Theme not found for work: ${workId}`);
      setTheme('default');
    }
  }, [setTheme]);

  /**
   * Reset to default theme
   */
  const resetTheme = useCallback(() => {
    setTheme('default');
  }, [setTheme]);

  /**
   * Set novel reading theme
   */
  const setNovelTheme = useCallback(() => {
    setTheme('novel');
  }, [setTheme]);

  /**
   * Apply CSS variables to document root
   */
  useEffect(() => {
    const root = document.documentElement;
    
    if (activeTheme?.cssVariables) {
      applyThemeVariables(root, activeTheme);
    }

    // Also set a data attribute for CSS targeting
    root.setAttribute('data-theme', activeThemeId);

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor && activeTheme?.colors?.primary) {
      metaThemeColor.setAttribute('content', activeTheme.colors.primary);
    }
  }, [activeTheme, activeThemeId]);

  /**
   * Get theme by ID
   */
  const getThemeById = useCallback((themeId) => {
    if (themeId === 'default') return defaultTheme;
    if (themeId === 'novel') return novelTheme;
    return workThemes[themeId] || defaultTheme;
  }, []);

  /**
   * Check if a theme is active
   */
  const isThemeActive = useCallback((themeId) => {
    return activeThemeId === themeId;
  }, [activeThemeId]);

  /**
   * Get all available themes
   */
  const allThemes = useMemo(() => {
    return {
      default: defaultTheme,
      novel: novelTheme,
      ...workThemes,
    };
  }, []);

  /**
   * Get CSS variable value for current theme
   */
  const getCssVariable = useCallback((variableName) => {
    if (!activeTheme?.cssVariables) return null;
    return activeTheme.cssVariables[variableName] || null;
  }, [activeTheme]);

  /**
   * Generate inline styles for themed components
   */
  const getThemedStyles = useCallback((styleType) => {
    if (!activeTheme) return {};

    switch (styleType) {
      case 'background':
        return {
          background: activeTheme.gradients?.backgroundDark || activeTheme.gradients?.background,
        };
      
      case 'card':
        return {
          background: `rgba(${activeTheme.cssVariables?.['--theme-accent-rgb'] || '255, 255, 255'}, 0.05)`,
          borderColor: `rgba(${activeTheme.cssVariables?.['--theme-accent-rgb'] || '255, 255, 255'}, 0.1)`,
        };
      
      case 'button':
        return {
          background: activeTheme.colors?.accent,
          color: '#ffffff',
        };
      
      case 'buttonOutline':
        return {
          borderColor: activeTheme.colors?.accent,
          color: activeTheme.colors?.accent,
        };
      
      case 'glow':
        return {
          boxShadow: activeTheme.glows?.medium,
        };
      
      case 'text':
        return {
          color: activeTheme.colors?.accent,
        };
      
      case 'gradient-text':
        return {
          background: activeTheme.gradients?.text,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        };
      
      default:
        return {};
    }
  }, [activeTheme]);

  /**
   * Get orb gradient for landing page
   */
  const getOrbGradient = useCallback((workId) => {
    const theme = workThemes[workId];
    if (!theme) return defaultTheme.gradients.orb;
    return theme.gradients.orb;
  }, []);

  /**
   * Get glow color for a specific work
   */
  const getWorkGlow = useCallback((workId, size = 'medium') => {
    const theme = workThemes[workId];
    if (!theme) return defaultTheme.glows[size];
    return theme.glows[size] || theme.glows.medium;
  }, []);

  // Context value
  const value = {
    // State
    activeTheme,
    activeThemeId,
    isTransitioning,
    allThemes,
    
    // Theme setters
    setTheme,
    setWorkTheme,
    setNovelTheme,
    resetTheme,
    
    // Getters
    getThemeById,
    isThemeActive,
    getCssVariable,
    getThemedStyles,
    getOrbGradient,
    getWorkGlow,
    
    // Direct access to themes
    workThemes,
    defaultTheme,
    novelTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {/* Transition overlay */}
      {isTransitioning && (
        <div 
          className="fixed inset-0 z-[9999] pointer-events-none bg-black/20 transition-opacity duration-300"
          style={{ opacity: isTransitioning ? 1 : 0 }}
        />
      )}
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Custom hook to use theme context
 * @returns {Object} Theme context value
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};

/**
 * Custom hook to get theme for a specific work
 * @param {string} workId - Work identifier
 * @returns {Object} Work theme configuration
 */
export const useWorkTheme = (workId) => {
  const { getThemeById, setWorkTheme } = useTheme();
  
  const theme = useMemo(() => {
    return getThemeById(workId);
  }, [workId, getThemeById]);

  // Set theme when workId changes
  useEffect(() => {
    if (workId) {
      setWorkTheme(workId);
    }
  }, [workId, setWorkTheme]);

  return theme;
};

/**
 * HOC to wrap component with theme
 * @param {React.Component} Component - Component to wrap
 * @param {string} themeId - Theme ID to apply
 * @returns {React.Component} Wrapped component
 */
export const withTheme = (Component, themeId) => {
  return function ThemedComponent(props) {
    const { setTheme, resetTheme } = useTheme();

    useEffect(() => {
      setTheme(themeId);
      return () => resetTheme();
    }, [setTheme, resetTheme]);

    return <Component {...props} />;
  };
};

export default ThemeContext;
